import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings } from '../../../types/types';
import { capitalizeFirstLetter, formatLabel, hexToRgba } from '../../../utils/helper';
import StyledInput from '../../StyledComponents/StyledInput';
import AMSSelect from '../../AMSSelect';
import { useGetCriteriaConditionsQuery, useLazyGetContactRolesQuery } from '../../../api/searchQueryApiSlice';
import { useLazyGetOrganizationsMinimalQuery } from '../../../api/organizationApiSlice';
import AddIcon from '../../../assets/icons/AddIcon';
import DeleteIcon from '../../../assets/icons/DeleteIcon';
import DraggableIcon from '../../../assets/icons/DraggableIcon';
import CalendarIcon from '../../../assets/icons/CalendarIcon';
import { getIconForDataType } from '../../FormBuilder/FieldSettings/fieldSettingsHelper';
import CustomInput from '../../form/CustomInput';
import { usePaginatedQuery } from '../../../hooks/usePaginatedOptions';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';

type CriteriaLogicValue = 'All' | 'Any';

type Column = {
    field: string;
    label: string;
    dataType: string;
    options?: { label?: string; value?: string | number }[];
    [key: string]: any;
};

type FieldOption = {
    id: string | number;
    label: string;
    value: string;
    dataType?: string;
    accessKey?: string;
};

type ValueOption = {
    id: string | number;
    label: string;
    value: string | number;
    name?: string;
};

type PublicDirectoryQueryPayload = {
    selectedColumns: string[];
    criteria: {
        logic: 'AND' | 'OR';
        conditions: Array<{
            field: string;
            operator: string;
            value: any;
        }>;
    };
};

const COLUMNS_PER_PAGE = 50;

const mapColumnToOption = (col: any): FieldOption | null => {
    const value = col?.field ?? col?.accessKey ?? col?.value ?? col?.id;
    if (!value) return null;

    const label = col?.label ?? formatLabel(String(value));

    return {
        label,
        value: String(value),
        dataType: col?.dataType,
        icon: getIconForDataType(col?.dataType),
        id: col?.id ?? value,
        accessKey: col?.accessKey ?? String(value),
    } as FieldOption;
};

const mapColumnToMeta = (col: any): Column | null => {
    const field = col?.field ?? col?.accessKey ?? col?.value ?? col?.id;
    if (!field) return null;

    const options = col?.options ?? col?.choices ?? col?.selectOptions ?? col?.values ?? col?.items ?? [];

    return {
        ...col,
        field: String(field),
        label: col?.label ?? formatLabel(String(field)),
        dataType: col?.dataType ?? '',
        options,
    } as Column;
};

const extractColumnsRows = (payload: any): Column[] => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload as Column[];
    if (Array.isArray(payload?.items)) return payload.items as Column[];
    if (Array.isArray(payload?.results)) return payload.results as Column[];
    if (Array.isArray(payload?.data)) return payload.data as Column[];
    if (Array.isArray(payload?.data?.items)) return payload.data.items as Column[];
    if (Array.isArray(payload?.data?.results)) return payload.data.results as Column[];
    return [];
};

type Condition = {
    name: string;
    friendlyName: string;
    valueType: string;
    expectedValueType: string[];
};

type CriteriaValue =
    | string
    | number
    | string[]
    | number[]
    | Array<string | number>
    | boolean
    | {
        start?: string | null;
        end?: string | null;
    }
    | null;

interface ConditionValues {
    id: number;
    column: string;
    condition: string;
    value: CriteriaValue;
}

const generateCriteriaId = () => new Date().getTime() + Math.random();

const createEmptyCondition = (): ConditionValues => ({
    id: generateCriteriaId(),
    column: '',
    condition: '',
    value: '',
});

const buildPublicDirectoryPayload = (
    selectedFields: FieldOption[],
    conditions: ConditionValues[],
    logic: CriteriaLogicValue,
    scope: string | undefined,
    conditionOptions: Condition[] = []
) => {
    const normalizedFields = normalizeSelectedFieldValues(selectedFields);

    // Convert logic: "All" -> "AND", "Any" -> "OR"
    const logicValue = logic === 'All' ? 'AND' : 'OR';

    // Build selectedColumns array from selectedFields
    const selectedColumns = normalizedFields.map((field) => field.value);

    // Filter out empty conditions (incomplete rules) and transform them
    const validConditions = conditions.filter((cond) => cond.column && cond.condition);

    // Transform conditions to proper format for backend
    const criteriaConditions = validConditions.map((cond) => {
        const transformed = transformConditionForSubmit(cond, conditionOptions);
        return {
            field: transformed.column,
            operator: transformed.condition,
            value: transformed.value ?? '',
        };
    });

    return {
        selectedColumns,
        criteria: {
            logic: logicValue,
            conditions: criteriaConditions,
        }
    };
};

const resolveLogicValue = (value?: any): CriteriaLogicValue => {
    if (!value) return 'All';
    if (typeof value === 'object') {
        const raw = value?.value ?? value?.label;
        if (raw === 'Any' || raw === 'OR') return 'Any';
        if (raw === 'All' || raw === 'AND') return 'All';
    }
    if (value === 'Any' || value === 'OR') return 'Any';
    return 'All';
};

const normalizeConditionValue = (item: any): ConditionValues => {
    if (!item) {
        return createEmptyCondition();
    }

    const column = item.column ?? item.field ?? '';
    const condition = item.condition ?? item.operator ?? '';
    let val = item.value ?? '';

    if (Array.isArray(val) && val.length === 2 && condition === 'between') {
        val = { start: val[0], end: val[1] };
    }

    return {
        id: item.id ?? generateCriteriaId(),
        column,
        condition,
        value: val,
    };
};

const normalizeCriteriaItems = (criteriaList?: any[]): ConditionValues[] => {
    if (!Array.isArray(criteriaList) || criteriaList.length === 0) {
        return [createEmptyCondition()];
    }

    const flattened = criteriaList.flatMap((item: any) => {
        const isGroupLike = item?.isGroup || (item?.logic && Array.isArray(item?.conditions) && !item?.column && !item?.field);
        if (isGroupLike) {
            return (item?.conditions || []).map((child: any) => normalizeConditionValue(child));
        }
        return normalizeConditionValue(item);
    });

    return flattened.length ? flattened : [createEmptyCondition()];
};

const transformConditionForSubmit = (item: ConditionValues, conditionOptions: Condition[]): ConditionValues => {
    const matchedCondition = (conditionOptions ?? []).find((cond: Condition) => cond.name === item.condition);

    let val = item.value;

    if (matchedCondition?.valueType === 'none' || item.condition === 'isNull' || item.condition === 'isNotNull') {
        return { ...item, value: null };
    }

    if (val && typeof val === 'object' && !Array.isArray(val) && 'start' in val && 'end' in val) {
        val = [(val as any).start, (val as any).end];
    } else if (typeof val === 'boolean') {
        val = val.toString();
    }

    return { ...item, value: val };
};

const normalizeOptions = (rawOptions: any): Array<{ label: string; value: string | number; id: string | number }> => {
    if (!Array.isArray(rawOptions)) return [];

    return rawOptions
        .map((opt: any) => {
            if (opt && typeof opt === 'object') {
                const value = opt?.value ?? opt?.label ?? opt?.name;
                const label = opt?.label ?? opt?.name ?? opt?.value;
                if (value === undefined || value === null) return null;
                return {
                    label: String(label ?? value),
                    value,
                    id: opt?.id ?? value,
                };
            }

            return {
                label: String(opt),
                value: opt,
                id: opt,
            };
        })
        .filter(Boolean) as Array<{ label: string; value: string | number; id: string | number }>;
};

const normalizeDataType = (rawType?: string) => {
    const normalized = String(rawType || '').trim().toLowerCase();

    if (!normalized) return '';
    if (normalized.startsWith('multi-select') || normalized.startsWith('multiselect')) return 'multi-select';
    if (normalized.startsWith('select')) return 'select';
    if (normalized.startsWith('string') || normalized.startsWith('text')) return 'string';
    if (normalized.startsWith('number') || normalized.startsWith('integer') || normalized.startsWith('float') || normalized.startsWith('decimal')) return 'number';
    if (normalized.startsWith('date') || normalized.startsWith('datetime')) return 'date';
    if (normalized.startsWith('boolean') || normalized.startsWith('bool')) return 'boolean';

    return normalized;
};

const normalizeSelectedFieldItems = (items?: any[]): FieldOption[] => {
    if (!Array.isArray(items)) return [];

    return items
        .map((item: any) => {
            if (!item) return null;
            const label = item.label ?? item.name ?? item.field ?? item.value;
            const value = item.value ?? item.field ?? item.id ?? '';
            if (!value) return null;

            return {
                id: item.id ?? value,
                label: String(label ?? value),
                value: String(value),
                dataType: item.dataType,
                accessKey: item.accessKey ?? item.field,
            } as FieldOption;
        })
        .filter(Boolean) as FieldOption[];
};

const normalizeSelectedColumns = (items?: any[]): FieldOption[] => {
    if (!Array.isArray(items)) return [];

    return items
        .map((item: any) => {
            if (item === undefined || item === null || item === '') return null;

            if (typeof item === 'string' || typeof item === 'number') {
                const value = String(item);
                return {
                    id: value,
                    label: formatLabel(value),
                    value,
                } as FieldOption;
            }

            const value = item.value ?? item.field ?? item.id ?? item.accessKey ?? '';
            if (!value) return null;

            return {
                id: item.id ?? value,
                label: String(item.label ?? item.name ?? formatLabel(String(value))),
                value: String(value),
                dataType: item.dataType,
                accessKey: item.accessKey ?? item.field ?? String(value),
            } as FieldOption;
        })
        .filter(Boolean) as FieldOption[];
};

const rehydrateSelectedFieldItems = (items: FieldOption[], availableOptions: FieldOption[]) => {
    if (!Array.isArray(items) || !items.length || !Array.isArray(availableOptions) || !availableOptions.length) {
        return items;
    }

    const optionByKey = new Map<string, FieldOption>();

    availableOptions.forEach((option) => {
        const valueKey = String(option.value ?? '').trim();
        const accessKey = String(option.accessKey ?? '').trim();

        if (valueKey) {
            optionByKey.set(valueKey, option);
        }

        if (accessKey) {
            optionByKey.set(accessKey, option);
        }
    });

    return items.map((item) => {
        const lookupKey = String(item.value ?? item.accessKey ?? '').trim();
        const matched = lookupKey ? optionByKey.get(lookupKey) : undefined;

        if (!matched) {
            return item;
        }

        return {
            ...item,
            id: matched.id ?? item.id,
            label: matched.label ?? item.label,
            value: String(matched.value ?? item.value),
            dataType: matched.dataType ?? item.dataType,
            accessKey: matched.accessKey ?? item.accessKey ?? String(matched.value ?? item.value),
        } as FieldOption;
    });
};

const areSelectedFieldDetailsEqual = (a: FieldOption[], b: FieldOption[]) =>
    JSON.stringify(
        a.map((item) => ({
            id: String(item.id ?? ''),
            label: String(item.label ?? ''),
            value: String(item.value ?? ''),
            dataType: String(item.dataType ?? ''),
            accessKey: String(item.accessKey ?? ''),
        }))
    ) ===
    JSON.stringify(
        b.map((item) => ({
            id: String(item.id ?? ''),
            label: String(item.label ?? ''),
            value: String(item.value ?? ''),
            dataType: String(item.dataType ?? ''),
            accessKey: String(item.accessKey ?? ''),
        }))
    );

const normalizeQueryPayload = (settings?: TempSettings): PublicDirectoryQueryPayload | undefined => {
    const rawPayload = settings?.queryPayload ?? settings?.publicDirectoryPayload;
    if (!rawPayload || typeof rawPayload !== 'object') {
        return undefined;
    }

    return {
        selectedColumns: Array.isArray(rawPayload?.selectedColumns)
            ? rawPayload.selectedColumns.map((item: any) => String(item))
            : [],
        criteria: {
            logic: rawPayload?.criteria?.logic === 'OR' ? 'OR' : 'AND',
            conditions: Array.isArray(rawPayload?.criteria?.conditions)
                ? rawPayload.criteria.conditions.map((condition: any) => ({
                    field: String(condition?.field ?? condition?.column ?? ''),
                    operator: String(condition?.operator ?? condition?.condition ?? ''),
                    value: condition?.value ?? ''
                }))
                : []
        }
    };
};

const serializeSelectedFields = (items: FieldOption[]) =>
    normalizeSelectedFieldValues(items).map((item) => ({
        label: item.label,
        value: item.value,
        dataType: item.dataType,
        accessKey: item.accessKey,
    }));

const areFieldListsEqual = (a: FieldOption[], b: FieldOption[]) =>
    JSON.stringify(a.map((item) => item.value)) === JSON.stringify(b.map((item) => item.value));

const areValuesEqual = (a: CriteriaValue, b: CriteriaValue) => JSON.stringify(a) === JSON.stringify(b);

const areConditionsEqual = (a: ConditionValues[], b: ConditionValues[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i].column !== b[i].column) return false;
        if (a[i].condition !== b[i].condition) return false;
        if (!areValuesEqual(a[i].value, b[i].value)) return false;
    }
    return true;
};

const isHiddenAppliedField = (field: FieldOption) => {
    const normalizedLabel = String(field.label || '').trim().toLowerCase();
    const normalizedValue = String(field.value || '').trim().toLowerCase();
    return normalizedLabel === 'id' || normalizedValue === 'id' || normalizedValue === 'contact_id' || normalizedValue === 'contactid';
};

const normalizeSelectedFieldValues = (items: FieldOption[]) => {
    const seen = new Set<string>();

    return items.filter((field) => {
        if (isHiddenAppliedField(field)) {
            return false;
        }

        const key = String(field.value || '').trim();
        if (!key) {
            return false;
        }

        if (seen.has(key)) {
            return false;
        }

        seen.add(key);
        return true;
    });
};

const getVisibleSelectedFields = (items: FieldOption[]) => items.filter((field) => !isHiddenAppliedField(field));

const reorderVisibleSelectedFields = (items: FieldOption[], sourceIndex: number, destinationIndex: number) => {
    const visibleFields = getVisibleSelectedFields(items);

    if (
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= visibleFields.length ||
        destinationIndex >= visibleFields.length ||
        sourceIndex === destinationIndex
    ) {
        return items;
    }

    const reorderedVisibleFields = [...visibleFields];
    const [movedField] = reorderedVisibleFields.splice(sourceIndex, 1);
    reorderedVisibleFields.splice(destinationIndex, 0, movedField);

    let visibleIndex = 0;

    return items.map((field) => {
        if (isHiddenAppliedField(field)) {
            return field;
        }

        const nextField = reorderedVisibleFields[visibleIndex];
        visibleIndex += 1;
        return nextField ?? field;
    });
};

const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
    let currentElement = element?.parentElement ?? null;

    while (currentElement) {
        const style = window.getComputedStyle(currentElement);
        const overflowY = style.overflowY;

        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && currentElement.scrollHeight > currentElement.clientHeight) {
            return currentElement;
        }

        currentElement = currentElement.parentElement;
    }

    return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
};

const hasVisibleSelectedFields = (items: FieldOption[]) => items.filter((field) => !isHiddenAppliedField(field)).length > 0;

const PublicDirectoryProperties = ({ updateTempSetting, tempSettings }: { updateTempSetting: (key: string, value: any) => void, tempSettings: TempSettings }) => {
    const { theme } = useTheme();
    const scopeOptions = [{ value: 'contact', Label: 'Contact' }, { value: 'organization', Label: 'Organization' }];
    const initialQueryPayload = normalizeQueryPayload(tempSettings);
    const [logic, setLogic] = useState<CriteriaLogicValue>(resolveLogicValue(initialQueryPayload?.criteria?.logic ?? tempSettings?.logic));
    const [conditions, setConditions] = useState<ConditionValues[]>(normalizeCriteriaItems(initialQueryPayload?.criteria?.conditions ?? tempSettings?.criteria));
    const [selectedFields, setSelectedFields] = useState<FieldOption[]>(normalizeSelectedColumns(initialQueryPayload?.selectedColumns ?? tempSettings?.selectedFields));
    const [columnMetaByField, setColumnMetaByField] = useState<Record<string, Column>>({});
    const [columnsPreloadAttempted, setColumnsPreloadAttempted] = useState(false);
    const prevScopeRef = useRef<string | undefined>(tempSettings.scope);
    const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);
    const [showFieldValidationError, setShowFieldValidationError] = useState(false);

    const { data: conditionOptions = [] } = useGetCriteriaConditionsQuery();
    const {
        items: roles,
        isLoading: rolesLoading,
        hasMore: rolesHasMore,
        fetchNextPage: fetchRolesNextPage,
    } = usePaginatedQuery({
        lazyHook: useLazyGetContactRolesQuery,
        selectItems: (data) => data?.roles || [],
        perPage: 10,
    });

    const {
        items: organizations,
        isLoading: organizationsLoading,
        hasMore: organizationsHasMore,
        fetchNextPage: fetchOrganizationsNextPage,
    } = usePaginatedQuery({
        lazyHook: useLazyGetOrganizationsMinimalQuery,
        selectItems: (data) => data?.organizations || [],
        perPage: 10,
    });

    // Deduplicate roles to prevent duplicate entries in dropdown
    const deduplicatedRoles = useMemo(() => {
        const seen = new Set<string>();
        return roles.filter((role: any) => {
            const roleId = String(role?.id ?? role?.name ?? '');
            if (seen.has(roleId)) {
                return false;
            }
            seen.add(roleId);
            return true;
        });
    }, [roles]);

    // Deduplicate organizations to prevent duplicate entries in dropdown
    const deduplicatedOrganizations = useMemo(() => {
        const seen = new Set<string>();
        return organizations.filter((org: any) => {
            const orgId = String(org?.id ?? org?.name ?? '');
            if (seen.has(orgId)) {
                return false;
            }
            seen.add(orgId);
            return true;
        });
    }, [organizations]);

    useEffect(() => {
        if (prevScopeRef.current !== undefined && prevScopeRef.current !== tempSettings.scope) {
            updateTempSetting('searchQueryId', undefined);
            updateTempSetting('searchQueryName', undefined);
            updateTempSetting('queryPayload', undefined);
            updateTempSetting('publicDirectoryPayload', undefined);
            setLogic('All');
            setConditions([createEmptyCondition()]);
            setSelectedFields([]);
            setColumnMetaByField({});
        }
        prevScopeRef.current = tempSettings.scope;
    }, [tempSettings.scope, updateTempSetting]);

    const mapColumnsResponse = useCallback((json: any, page: number) => {
        const rows = extractColumnsRows(json);
        const totalCount = json?.totalCount ?? json?.pagination?.total ?? json?.data?.totalCount ?? json?.data?.pagination?.total ?? 0;
        const perPage = json?.perPage ?? json?.pagination?.perPage ?? json?.data?.perPage ?? json?.data?.pagination?.perPage ?? COLUMNS_PER_PAGE;
        const currentPage = json?.page ?? json?.pagination?.page ?? json?.data?.page ?? json?.data?.pagination?.page ?? page;

        const options = rows.map(mapColumnToOption).filter(Boolean) as FieldOption[];
        const metaRows = rows.map(mapColumnToMeta).filter(Boolean) as Column[];

        if (metaRows.length) {
            setColumnMetaByField((prev) => {
                const next = { ...prev };
                metaRows.forEach((col) => {
                    next[String(col.field)] = col;
                });
                return next;
            });
        }

        const hasMore = totalCount
            ? currentPage * perPage < totalCount
            : rows.length === perPage;

        return {
            options,
            hasMore,
            additional: { page: currentPage + 1 },
        };
    }, []);

    // Fetch column metadata for existing conditions on mount (for edit mode)
    useEffect(() => {
        if (tempSettings.scope && conditions.length > 0 && Object.keys(columnMetaByField).length === 0 && !columnsPreloadAttempted) {
            // Trigger fetch of columns data in the background
            const hasExistingConditions = conditions.some(cond => cond.column);
            if (hasExistingConditions) {
                // Fetch columns to populate metadata with proper dataTypes
                const params = new URLSearchParams({
                    scope: tempSettings.scope,
                    page: '1',
                    perPage: '100'
                });

                fetch(`search-query/columns?${params.toString()}`)
                    .then(async (res) => {
                        const ct = res.headers.get('content-type') || '';
                        if (ct.includes('application/json')) {
                            return res.json();
                        }
                        // If server returned HTML (e.g. auth page or 404), read as text and bail gracefully
                        const text = await res.text();
                        console.warn('Unexpected non-JSON response when preloading columns:', text?.slice(0, 300));
                        return null;
                    })
                    .then(data => {
                        if (data) {
                            mapColumnsResponse(data, 1);

                        } else {
                            // Mark that we attempted preload to avoid retry loops
                            console.warn('No JSON data returned when preloading columns; skipping further preload attempts.');
                        }
                        setColumnsPreloadAttempted(true);
                    })
                    .catch(err => {
                        console.error('Failed to preload column metadata:', err);
                        setColumnsPreloadAttempted(true);
                    });
            }
        }
    }, [tempSettings.scope, conditions.length, mapColumnsResponse, columnsPreloadAttempted]);

    useEffect(() => {
        const nextSelected = normalizeSelectedFieldItems(tempSettings?.selectedFields);
        if (!areFieldListsEqual(nextSelected, selectedFields)) {
            updateTempSetting('selectedFields', serializeSelectedFields(selectedFields));
        }
    }, [selectedFields, tempSettings?.selectedFields, updateTempSetting]);

    useEffect(() => {
        const converted = conditions.map((item) => transformConditionForSubmit(item, conditionOptions as Condition[]));

        const currentCriteria = normalizeCriteriaItems(tempSettings?.criteria).map((item) =>
            transformConditionForSubmit(item, conditionOptions as Condition[])
        );
        const currentLogic = resolveLogicValue(tempSettings?.logic);

        if (!areConditionsEqual(currentCriteria, converted)) {
            updateTempSetting('criteria', converted);
        }

        if (currentLogic !== logic) {
            updateTempSetting('logic', { label: logic, value: logic });
        }
    }, [conditions, logic, conditionOptions, tempSettings?.criteria, tempSettings?.logic, updateTempSetting]);

    // Build and update the complete payload for Save/Publish
    useEffect(() => {
        const payload = buildPublicDirectoryPayload(selectedFields, conditions, logic, tempSettings.scope, conditionOptions as Condition[]);
        try {
            const existingRaw = tempSettings?.queryPayload ?? tempSettings?.publicDirectoryPayload;
            const existing = existingRaw && typeof existingRaw === 'object'
                ? {
                    selectedColumns: Array.isArray(existingRaw.selectedColumns) ? existingRaw.selectedColumns.map((i: any) => String(i)) : [],
                    criteria: {
                        logic: existingRaw?.criteria?.logic === 'OR' ? 'OR' : 'AND',
                        conditions: Array.isArray(existingRaw?.criteria?.conditions) ? existingRaw.criteria.conditions : []
                    }
                }
                : null;

            if (JSON.stringify(existing) !== JSON.stringify(payload)) {
                updateTempSetting('queryPayload', payload);
            }
        } catch (e) {
            console.warn('Failed to compare existing query payloads, updating anyway', e);
            updateTempSetting('queryPayload', payload);
        }
    }, [selectedFields, conditions, logic, tempSettings.scope, conditionOptions, updateTempSetting]);

    const updateCondition = (id: number, patch: Partial<ConditionValues>) => {
        setConditions((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    };

    const removeCondition = (id: number) => {
        setConditions((prev) => {
            const next = prev.filter((item) => item.id !== id);
            return next.length ? next : [createEmptyCondition()];
        });
    };

    const getValidConditionsForColumn = (column: Column | undefined, operators: Condition[]) => {
        if (!column?.dataType) return [];

        const dataType = normalizeDataType(column.dataType);

        const baseOps = (() => {
            switch (dataType) {
                case 'string':
                    return operators.filter((op: Condition) => op.valueType === 'single' && op.expectedValueType.includes('string'));
                case 'number':
                    return operators.filter((op: Condition) => ['single', 'array (length = 2)'].includes(op.valueType) && op.expectedValueType.includes('number'));
                case 'date':
                    return operators.filter((op: Condition) => op.expectedValueType.includes('date'));
                case 'boolean':
                    return operators.filter((op: Condition) => op.valueType === 'single' && op.expectedValueType.includes('boolean'));
                case 'select':
                case 'multi-select':
                    return operators.filter((op: Condition) => op.valueType === 'array' && (op.expectedValueType.includes('string') || op.expectedValueType.includes('number')));
                default:
                    return [];
            }
        })();

        const nullOps = operators.filter((op: Condition) => op.name === 'isNull' || op.name === 'isNotNull');
        const combined = [...baseOps, ...nullOps];
        return Array.from(new Map(combined.map((op) => [op.name, op])).values());
    };

    const getValueFieldType = (column?: Column, condition?: Condition): 'text' | 'select' | 'date' | 'dateRange' | 'number' | 'numberRange' | 'checkBox' | 'none' => {
        if (!column || !condition) return 'none';

        const dataType = normalizeDataType(column.dataType);
        const { valueType, expectedValueType } = condition;
        const includes = (types: string[]) => types.some((t) => expectedValueType.includes(t));

        if (valueType === 'single' && dataType === 'string' && includes(['string', 'number'])) return 'text';
        if (valueType === 'single' && dataType === 'number' && includes(['number'])) return 'number';
        if (valueType === 'array (length = 2)' && dataType === 'number' && includes(['number'])) return 'numberRange';
        if ((valueType === 'array' || valueType === 'single') && (dataType === 'select' || dataType === 'multi-select') && includes(['string', 'number'])) return 'select';
        if ((valueType === 'array' || valueType === 'single') && dataType === 'date' && includes(['string', 'number', 'date'])) return 'date';
        if (valueType === 'array (length = 2)' && dataType === 'date' && includes(['number', 'date'])) return 'dateRange';
        if (valueType === 'single' && dataType === 'boolean' && includes(['boolean'])) return 'checkBox';

        return 'none';
    };

    const normalizedColumns = useMemo<Column[]>(() => {
        return Object.values(columnMetaByField);
    }, [columnMetaByField]);

    const columnOptions = useMemo(() => {
        return normalizedColumns.map(mapColumnToOption).filter(Boolean) as FieldOption[];
    }, [normalizedColumns]);

    useEffect(() => {
        if (!columnOptions.length || !selectedFields.length) {
            return;
        }

        const hydratedSelectedFields = rehydrateSelectedFieldItems(selectedFields, columnOptions);
        if (!areSelectedFieldDetailsEqual(selectedFields, hydratedSelectedFields)) {
            setSelectedFields(hydratedSelectedFields);
        }
    }, [columnOptions, selectedFields]);

    useEffect(() => {
        const handleShowValidationError = () => {
            setShowFieldValidationError(true);
        };

        window.addEventListener('showFieldValidationError', handleShowValidationError);
        return () => {
            window.removeEventListener('showFieldValidationError', handleShowValidationError);
        };
    }, []);

    const handleFieldSelect = (options: any) => {
        const nextSelected = Array.isArray(options)
            ? options.map((option: any) => ({
                id: option.id ?? option.value,
                label: String(option.label ?? option.value),
                value: String(option.value),
                dataType: option.dataType,
                accessKey: option.accessKey ?? option.value,
            }))
            : [];

        setSelectedFields(normalizeSelectedFieldValues(nextSelected));
    };

    const handleSelectedFieldDragStart = (fieldId: string) => {
        setDraggedFieldId(fieldId);
    };

    const handleSelectedFieldDragEnd = (result: DropResult) => {
        if (result.destination == null) {
            setDraggedFieldId(null);
            return;
        }

        const destination = result.destination;

        if (result.source.index !== destination.index) {
            setSelectedFields((prev) => reorderVisibleSelectedFields(prev, result.source.index, destination.index));
        }

        setDraggedFieldId(null);
    };

    const removeSelectedField = (fieldValue: string) => {
        setSelectedFields((prev) => prev.filter((field) => String(field.value) !== String(fieldValue)));
    };

    return (
        <Container theme={theme}>
            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Content</StyledText>

            <Section theme={theme} className='mt-4 pt-2'>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Select Public Directory</StyledText>
                <div>
                    <AMSSelect
                        showIcons
                        staticOptions={scopeOptions.map((scope) => ({ value: scope.value, label: scope.Label }))}
                        placeholder='Select public directory here'
                        value={tempSettings.scope ? { value: tempSettings.scope, label: capitalizeFirstLetter(tempSettings.scope) } : undefined}
                        onChange={(option) => {
                            updateTempSetting('scope', (
                                option?.value !== undefined && option?.value !== null
                                    ? String(option.value)
                                    : undefined
                            ));
                        }}
                        defaultOptions
                    />
                </div>
                <StyledText fontSize='12px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Select one directory to display on this page.</StyledText>

            </Section>

            <StyledHorizontalLine theme={theme} className='mt-4 mb-3' />

            <Section theme={theme} className='mt-3'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Select Fields That You Want To Display</StyledText>
                    <StyledText fontSize='16px' fontWeight={600} textColor={theme.CreateFormTheme.colors.required}>*</StyledText>
                </div>
                <AMSSelect
                    key={`all-fields-${tempSettings.scope || 'no-scope'}`}
                    isMulti
                    endpoint={tempSettings.scope ? 'search-query/columns' : undefined}
                    additionalParams={tempSettings.scope ? { scope: tempSettings.scope } : undefined}
                    mapResponse={mapColumnsResponse}
                    cacheUniqs={[tempSettings.scope || 'no-scope']}
                    defaultOptions={Boolean(tempSettings.scope)}
                    placeholder='Select multiple fields here'
                    value={selectedFields}
                    onChange={handleFieldSelect}
                    isSearchable
                    includeAccessKey
                    showDataType
                    menuPosition='fixed'
                    isDisabled={!tempSettings.scope}
                />
                {showFieldValidationError && !hasVisibleSelectedFields(selectedFields) && (
                    <StyledText textColor={theme.CorporateTheme.colors.danger} fontSize='12px' fontWeight={500} className='mt-1'>
                        Please select at least one column.
                    </StyledText>
                )}
            </Section>

            <Section theme={theme} className='mt-3'>
                {selectedFields.filter((field) => !isHiddenAppliedField(field)).length > 0 ? (
                    <ContentArrangementSection theme={theme}>
                        <DragDropContext onDragStart={(start) => handleSelectedFieldDragStart(start.draggableId)} onDragEnd={handleSelectedFieldDragEnd}>
                            <Droppable droppableId='public-directory-selected-fields' direction='vertical'>
                                {(provided) => (
                                    <ItemsList ref={provided.innerRef} {...provided.droppableProps}>
                                        {getVisibleSelectedFields(selectedFields).map((field, index) => (
                                            <Draggable key={field.value} draggableId={String(field.value)} index={index}>
                                                {(provided, snapshot) => (
                                                    <ItemCard
                                                        theme={theme}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        $isDragging={snapshot.isDragging}
                                                    >
                                                        <FieldDragHandle aria-hidden='true'>
                                                            <DraggableIcon />
                                                        </FieldDragHandle>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: 8 }}>
                                                            <ItemContent theme={theme} $isDragging={snapshot.isDragging}>
                                                                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                                                    {field.label}
                                                                </StyledText>
                                                            </ItemContent>
                                                            <DeleteButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeSelectedField(field.value);
                                                                }}
                                                                theme={theme}
                                                                title='Remove field'
                                                            >
                                                                <DeleteIcon color={theme.CorporateTheme.colors.headerColor} />
                                                            </DeleteButton>
                                                        </div>
                                                    </ItemCard>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </ItemsList>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </ContentArrangementSection>
                ) : null}
            </Section>

            <StyledHorizontalLine theme={theme} className='mt-4 mb-3' />

            {!tempSettings.scope || !hasVisibleSelectedFields(selectedFields) ? (
                <DisabledSection theme={theme} className='mt-3' style={{ background: theme.CorporateTheme.colors.filterBG, padding: '20px', opacity: 0.5, pointerEvents: 'none' }}>
                    <div className='d-flex align-items-center gap-2'>
                        <StyledText fontSize='12px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Condition Type :  </StyledText>
                        <ConditionTypeSwitch>
                            {(['All', 'Any'] as CriteriaLogicValue[]).map((option) => (
                                <OptionButton
                                    theme={theme}
                                    key={option}
                                    $isSelected={logic === option}
                                    onClick={() => setLogic(option)}
                                >
                                    {option.toUpperCase()}
                                </OptionButton>
                            ))}
                        </ConditionTypeSwitch>
                    </div>
                    <DisabledMessage theme={theme}>
                        <StyledText textColor={theme.CorporateTheme.colors.primary} fontSize='14px' fontWeight={500}>
                            {!tempSettings.scope ? 'Please select a Public Directory first.' : 'Please select at least one field to add conditions.'}
                        </StyledText>
                    </DisabledMessage>
                </DisabledSection>
            ) : (
                <Section theme={theme} className='mt-3' style={{ background: theme.CorporateTheme.colors.filterBG, padding: '20px' }}>
                    <div className='d-flex align-items-center gap-2'>
                        <StyledText fontSize='12px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Condition Type :  </StyledText>
                        <ConditionTypeSwitch>
                            {(['All', 'Any'] as CriteriaLogicValue[]).map((option) => (
                                <OptionButton
                                    theme={theme}
                                    key={option}
                                    $isSelected={logic === option}
                                    onClick={() => setLogic(option)}
                                >
                                    {option.toUpperCase()}
                                </OptionButton>
                            ))}
                        </ConditionTypeSwitch>
                    </div>

                    <RuleList>
                        {conditions.map((item, index) => {
                            const selectedColumn = normalizedColumns.find((col) => col.field === item.column);
                            const validConditions = getValidConditionsForColumn(selectedColumn, conditionOptions as Condition[]);
                            const selectedCondition = (conditionOptions as Condition[]).find((cond) => cond.name === item.condition);
                            const valueFieldType = getValueFieldType(selectedColumn, selectedCondition);

                            const selectOptions = normalizeOptions(
                                selectedColumn?.options ??
                                selectedColumn?.choices ??
                                selectedColumn?.selectOptions ??
                                selectedColumn?.values ??
                                selectedColumn?.items
                            );

                            const roleOptions: ValueOption[] = deduplicatedRoles.map((opt: any) => ({
                                id: opt.id,
                                value: opt.name,
                                label: opt.name,
                                name: opt.name,
                            }));

                            const organizationOptions: ValueOption[] = deduplicatedOrganizations.map((opt: any) => ({
                                id: opt.id,
                                value: opt.name,
                                label: opt.name,
                                name: opt.name,
                            }));

                            const isRoleField = selectedColumn?.dataType === 'multi-select' && selectedColumn?.field === 'r.name';
                            const isOrganizationField = selectedColumn?.dataType === 'multi-select' && selectedColumn?.field === 'o.name';
                            const valueOptions: ValueOption[] = isRoleField
                                ? roleOptions
                                : isOrganizationField
                                    ? organizationOptions
                                    : (selectOptions as ValueOption[]);

                            const isSelectMulti = selectedCondition?.valueType === 'array';
                            const currentSelectValue = isSelectMulti
                                ? valueOptions.filter((opt) => Array.isArray(item.value) && item.value.map(String).includes(String(opt.value)))
                                : valueOptions.find((opt) => String(opt.value) === String(item.value ?? ''));

                            return (
                                <RuleCard key={item.id} theme={theme}>
                                    <RuleHeader>
                                        <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.accentOrange}>Rule#{index + 1}</StyledText>
                                        {conditions.length > 1 && (
                                            <DeleteIcon
                                                color={theme.CorporateTheme.colors.textSecondary}
                                                size={16}
                                                className='cursor-pointer'
                                                onClick={() => removeCondition(item.id)}
                                            />
                                        )}
                                    </RuleHeader>

                                    <FieldGroup className='mt-3'>
                                        <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>If Field</StyledText>
                                        <AMSSelect
                                            key={`field-${tempSettings.scope || 'no-scope'}`}
                                            endpoint={tempSettings.scope ? 'search-query/columns' : undefined}
                                            additionalParams={tempSettings.scope ? { scope: tempSettings.scope } : undefined}
                                            mapResponse={mapColumnsResponse}
                                            cacheUniqs={[tempSettings.scope || 'no-scope']}
                                            defaultOptions={Boolean(tempSettings.scope)}
                                            value={columnOptions.find((opt: any) => String(opt.value) === String(item.column)) || null}
                                            placeholder='Select field'
                                            onChange={(opt: any) => {
                                                if (opt?.value) {
                                                    const meta = mapColumnToMeta(opt);
                                                    if (meta) {
                                                        setColumnMetaByField((prev) => ({
                                                            ...prev,
                                                            [String(meta.field)]: {
                                                                ...(prev[String(meta.field)] ?? {}),
                                                                ...meta,
                                                                options: prev[String(meta.field)]?.options ?? meta.options,
                                                            },
                                                        }));
                                                    }
                                                }
                                                updateCondition(item.id, {
                                                    column: opt?.value ? String(opt.value) : '',
                                                    condition: '',
                                                    value: '',
                                                });
                                            }}
                                            isSearchable
                                            menuPosition='fixed'
                                            includeAccessKey
                                            showDataType
                                            isDisabled={!tempSettings.scope}
                                        />
                                    </FieldGroup>

                                    <FieldGroup className='mt-3'>
                                        <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Select Operator</StyledText>
                                        <AMSSelect
                                            key={`operator-${item.id}-${item.column}-${Object.keys(columnMetaByField).length}`}
                                            staticOptions={validConditions.map((cond: Condition) => ({
                                                label: cond.friendlyName,
                                                value: cond.name,
                                            }))}
                                            value={validConditions
                                                .map((cond: Condition) => ({ label: cond.friendlyName, value: cond.name }))
                                                .find((opt: any) => opt.value === item.condition) || null}
                                            placeholder='Select operator'
                                            onChange={(opt: any) => {
                                                updateCondition(item.id, {
                                                    condition: opt?.value ? String(opt.value) : '',
                                                    value: '',
                                                });
                                            }}
                                            isSearchable
                                            menuPosition='fixed'
                                            isDisabled={!item.column}
                                        />
                                    </FieldGroup>

                                    {valueFieldType !== 'none' && (
                                        <FieldGroup className='mt-3'>
                                            <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Is Value</StyledText>

                                            {valueFieldType === 'text' && (
                                                <StyledInput
                                                    theme={theme}
                                                    placeholder={`Enter ${formatLabel(selectedColumn?.field || '')}`}
                                                    value={typeof item.value === 'string' ? item.value : ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCondition(item.id, { value: e.target.value })}
                                                    style={{ background: theme.CorporateTheme.colors.background }}

                                                />
                                            )}

                                            {valueFieldType === 'number' && (
                                                <StyledInput
                                                    theme={theme}
                                                    style={{ background: theme.CorporateTheme.colors.background }}
                                                    type='number'
                                                    min={0}
                                                    placeholder={`Enter ${formatLabel(selectedColumn?.field || '')}`}
                                                    value={typeof item.value === 'number' || typeof item.value === 'string' ? item.value : ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const raw = e.target.value;
                                                        updateCondition(item.id, { value: raw === '' ? '' : Number(raw) });
                                                    }}
                                                />
                                            )}

                                            {valueFieldType === 'numberRange' && (
                                                <RangeRow>
                                                    <StyledInput
                                                        theme={theme}
                                                        style={{ background: theme.CorporateTheme.colors.background }}
                                                        type='number'
                                                        min={0}
                                                        placeholder='Min'
                                                        value={Array.isArray(item.value) ? item.value[0] ?? '' : ''}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            const raw = e.target.value;
                                                            const current = Array.isArray(item.value) ? [...item.value] : ['', ''];
                                                            current[0] = raw === '' ? '' : Number(raw);
                                                            updateCondition(item.id, { value: current });
                                                        }}
                                                    />
                                                    <StyledInput
                                                        theme={theme}
                                                        style={{ background: theme.CorporateTheme.colors.background }}
                                                        type='number'
                                                        min={0}
                                                        placeholder='Max'
                                                        value={Array.isArray(item.value) ? item.value[1] ?? '' : ''}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            const raw = e.target.value;
                                                            const current = Array.isArray(item.value) ? [...item.value] : ['', ''];
                                                            current[1] = raw === '' ? '' : Number(raw);
                                                            updateCondition(item.id, { value: current });
                                                        }}
                                                    />
                                                </RangeRow>
                                            )}

                                            {valueFieldType === 'date' && (
                                                <CustomInput
                                                    type='date'
                                                    value={typeof item.value === 'string' ? item.value : ''}
                                                    onChange={(value) => updateCondition(item.id, { value })}
                                                    rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                                    placeholder='mm/dd/yyyy'
                                                />
                                            )}

                                            {valueFieldType === 'dateRange' && (
                                                <RangeRow>
                                                    <CustomInput
                                                        type='date'
                                                        value={item.value && typeof item.value === 'object' && !Array.isArray(item.value) ? item.value.start ?? '' : ''}
                                                        onChange={(value) => {
                                                            const current = item.value && typeof item.value === 'object' && !Array.isArray(item.value)
                                                                ? item.value
                                                                : { start: '', end: '' };
                                                            updateCondition(item.id, { value: { ...current, start: value } });
                                                        }}
                                                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                                        placeholder='Start date'
                                                    />
                                                    <CustomInput
                                                        type='date'
                                                        value={item.value && typeof item.value === 'object' && !Array.isArray(item.value) ? item.value.end ?? '' : ''}
                                                        onChange={(value) => {
                                                            const current = item.value && typeof item.value === 'object' && !Array.isArray(item.value)
                                                                ? item.value
                                                                : { start: '', end: '' };
                                                            updateCondition(item.id, { value: { ...current, end: value } });
                                                        }}
                                                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                                        placeholder='End date'
                                                    />
                                                </RangeRow>
                                            )}

                                            {valueFieldType === 'checkBox' && (
                                                <InlineCheckboxRow>
                                                    <input
                                                        type='checkbox'
                                                        checked={Boolean(item.value)}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCondition(item.id, { value: e.target.checked })}
                                                    />
                                                    <StyledText fontSize='12px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>
                                                        {formatLabel(selectedColumn?.label || selectedColumn?.field || '')}
                                                    </StyledText>
                                                </InlineCheckboxRow>
                                            )}

                                            {valueFieldType === 'select' && (
                                                <AMSSelect
                                                    key={`value-${item.id}-${item.column}-${item.condition}-${valueOptions.length}`}
                                                    isMulti={isSelectMulti}
                                                    staticOptions={valueOptions}
                                                    value={currentSelectValue || (isSelectMulti ? [] : null)}
                                                    onChange={(option: any) => {
                                                        if (isSelectMulti) {
                                                            const values = Array.isArray(option)
                                                                ? option.map((opt: any) => opt.value)
                                                                : [];
                                                            updateCondition(item.id, { value: values });
                                                        } else {
                                                            updateCondition(item.id, { value: option?.value ?? '' });
                                                        }
                                                    }}
                                                    placeholder='Select value'
                                                    isSearchable
                                                    menuPosition='fixed'
                                                    defaultOptions
                                                    isLoading={isRoleField ? rolesLoading : isOrganizationField ? organizationsLoading : false}
                                                />
                                            )}
                                        </FieldGroup>
                                    )}
                                </RuleCard>
                            );
                        })}
                    </RuleList>

                    <AddRuleButton
                        theme={theme}
                        type='button'
                        onClick={() => setConditions((prev) => [...prev, createEmptyCondition()])}
                        disabled={!tempSettings.scope}
                    >
                        <AddIcon color={theme.CorporateTheme.colors.primary} />
                        <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Add New Rule</StyledText>
                    </AddRuleButton>
                </Section>
            )}

        </Container>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
`

const Section = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;

const ContentArrangementSection = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ItemCard = styled.div<{ theme: Theme; $isDragging?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-radius: 8px;
    transition: all 0.2s ease;
    cursor: grab;
    opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
    user-select: none;
`;

const FieldDragHandle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
`;

const ItemContent = styled.div<{ theme: Theme; $isDragging?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 8px 10px;
    border-radius: 4px;
    gap: 4px;
    flex: 1;
    min-width: 0;
    height: 40px;
    border: 1px solid ${({ theme }) => hexToRgba(theme.CreateFormTheme.colors.inputBorders, 0.8)};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.filterBG};

    &:hover {
        border-color: ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &:active {
        cursor: grabbing;
    }
`;

const DeleteButton = styled.button<{ theme: Theme }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 11px;
    border-radius: 4px;
    transition: all 0.2s ease;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.filterBG};

    &:hover {
        opacity: 1;
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
        flex: 1;
        padding: 4px 12px;
        border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
        background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
        color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;
    
        /* Remove margin */
        margin: 0;
    
        /* Default no border-radius */
        border-radius: 0;
    
        /* First button - rounded left corners */
        &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
        }

        /* Last button - rounded right corners */
        &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
        }

        /* Remove double borders between buttons */
        &:not(:first-child) {
            margin-left: -1px;
        }

        /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
        position: relative;
        z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

        &:hover {
            background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
            border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
            color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
            z-index: 3; /* Higher z-index on hover */
        }

        /* Selected state styling */
        ${({ $isSelected, theme }) => $isSelected && `
            background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
            border-color: ${theme.CorporateTheme.colors.accentOrange};
            color: ${theme.CorporateTheme.colors.accentOrange};
            font-weight: 600;
            z-index: 2;
        `}
    `;

const ConditionTypeSwitch = styled.div`
    display: flex;
    align-items: center;
    width: fit-content;
    border-radius: 6px;
    overflow: hidden;
`;

const ConditionTypeButton = styled.button<{ $isSelected: boolean }>`
    border: none;
    padding: 4px 10px;
    min-width: 42px;
    font-size: 10px;
    font-weight: 500;
    cursor: pointer;
    text-align: center;
    color: ${({ theme, $isSelected }) =>
        $isSelected ? (theme?.CorporateTheme?.colors?.background || '#ffffff') : (theme?.CorporateTheme?.colors?.textSecondary || '#6b7280')};
    background: ${({ theme, $isSelected }) =>
        $isSelected ? (theme?.CorporateTheme?.colors?.accentOrange || '#f97316') : 'transparent'};
    transition: all 0.2s ease;

    &:hover {
        background: ${({ theme, $isSelected }) =>
        $isSelected
            ? (theme?.CorporateTheme?.colors?.accentOrange || '#f97316')
            : hexToRgba(theme?.CorporateTheme?.colors?.accentOrange || '#f97316', 0.1)};
    }
`;

const RuleList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const RuleCard = styled.div<{ theme: Theme }>`
    background: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
    border-radius: 6px;
    padding: 12px;
`;

const RuleHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const RangeRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

const InlineCheckboxRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 2px;
`;

const AddRuleButton = styled.button<{ theme: Theme }>`
    width: 100%;
    height: 54px;
    border: 1px dashed ${({ theme }) => theme?.CorporateTheme?.colors?.border || '#d9d9d9'};
    border-radius: 4px;
    background: ${({ theme }) => theme?.CorporateTheme?.colors?.background || '#6b7280'};;
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary || '#6b7280'};
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme?.CorporateTheme?.colors?.accentOrange || '#f97316'};
        color: ${({ theme }) => theme?.CorporateTheme?.colors?.accentOrange || '#f97316'};
        background: ${({ theme }) => hexToRgba(theme?.CorporateTheme?.colors?.accentOrange || '#f97316', 0.08)};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ValidationErrorText = styled.div<{ theme: Theme }>`
    margin-top: 8px;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: ${({ theme }) => hexToRgba(theme.CreateFormTheme.colors.required, 0.1)};
    border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.required};
    color: ${({ theme }) => theme.CreateFormTheme.colors.required};
    font-size: 12px;
    font-weight: 500;
`;

const DisabledSection = styled(Section)`
    opacity: 0.5;
    pointer-events: none;
`;

const DisabledMessage = styled.div<{ theme: Theme }>`
    margin-top: 12px;
    padding: 12px;
    border-radius: 4px;
    background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.primary, 0.1)};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.primary};
    color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    font-size: 12px;
    font-weight: 500;
    text-align: center;
`;

export default PublicDirectoryProperties